import { PromiseDelegate } from "promise-delegate";
import { KernelBase } from "@basthon/kernel-base";
import { KernelLoader } from "@basthon/kernel-loader";
/**
 * Base class for console and notebook GUI.
 */
export class GUIBase {
    constructor(kernelRootPath, language) {
        this._loaded = new PromiseDelegate();
        /* console errors redirected to notification system */
        this._console_error = console.error;
        this._language = language;
        this._loader = new KernelLoader(kernelRootPath, language);
        // loading Basthon (errors are fatal)
        this._loader.showLoader("Chargement de Basthon...", false, false);
    }
    /**
     * Language getter.
     */
    get language() { return this._language; }
    /**
     * Kernel getter.
     */
    get kernel() { return this._loader.kernel; }
    get kernelSafe() { return this._loader.kernelSafe; }
    /**
     * KernelLoader getter.
     */
    get kernelLoader() { return this._loader; }
    /**
     * Check if GUI is loaded.
     */
    async loaded() { await this._loaded.promise; }
    /**
     * Notify the user.
     */
    info(title, message) { }
    /**
     * Notify the user with an error.
     */
    error(title, message) { }
    /**
     * Ask the user to confirm or cancel.
     */
    confirm(title, message, text, callback, textCancel, callbackCancel) { }
    /**
     * Ask the user to select a choice.
     */
    select(title, message, choices, textCancel, callbackCancel) { }
    /**
     * The error notification system.
     */
    notifyError(error) {
        var _a, _b;
        this._console_error(error);
        let message = error.message;
        if (message == null)
            message = (_b = (_a = error) === null || _a === void 0 ? void 0 : _a.reason) === null || _b === void 0 ? void 0 : _b.message;
        if (message == null)
            message = error.toString();
        message = message.split('\n').join('<br>');
        this.error("Erreur", `Erreur : ${message}`);
        // In case of error, force loader hiding.
        try {
            this._loader.hideLoader();
        }
        catch (e) { }
    }
    /**
     * Initialize the GUI.
     */
    async init(options = null) {
        try {
            await this._init(options);
            this._loaded.resolve();
        }
        catch (error) {
            this._loaded.reject(error);
        }
    }
    async _init(options = null) {
        /* all errors redirected to notification system */
        const onerror = this.notifyError.bind(this);
        window.addEventListener('error', onerror);
        window.addEventListener("unhandledrejection", onerror);
        console.error = (message) => onerror(new ErrorEvent(message, { message: message }));
    }
    /**
     * Change loader text and call init function.
     * If catchError is false, in case of error, we continue the
     * init process, trying to do our best...
     */
    async initCaller(func, message, catchError) {
        this._loader.setLoaderText(message);
        try {
            return await func();
        }
        catch (error) {
            if (!catchError)
                throw error;
            this.notifyError(error);
        }
    }
    /**
     * Get mode as a string (dark/light).
     */
    theme() { return; }
    /**
     * Restart the kernel.
     */
    kernelRestart() { var _a; (_a = this.kernelSafe) === null || _a === void 0 ? void 0 : _a.restart(); }
    /**
     * Load ressources from URL (common part to files and modules).
     */
    async _loadFromURL(key, put) {
        const url = new URL(window.location.href);
        let promises = [];
        for (let fileURL of url.searchParams.getAll(key)) {
            fileURL = decodeURIComponent(fileURL);
            const filename = fileURL.split('/').pop() || "";
            let promise = GUIBase.xhr({
                method: "GET",
                url: fileURL,
                responseType: "arraybuffer"
            });
            promise = promise.then(function (data) {
                return put(filename, data);
            }).catch(function () {
                const message = `Impossible de charger le fichier ${filename}.`;
                throw new ErrorEvent(message, { message: message });
            });
            promises.push(promise);
        }
        await Promise.all(promises);
    }
    /**
     * Loading file in the (emulated) local filesystem (async).
     */
    async putFSRessource(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsArrayBuffer(file);
            reader.onload = async (event) => {
                var _a, _b, _c;
                (_a = this.kernelSafe) === null || _a === void 0 ? void 0 : _a.putRessource(file.name, reader.result);
                this.info(`Fichier utilisable depuis ${(_b = this.kernel) === null || _b === void 0 ? void 0 : _b.languageName()}`, `${file.name} est maintenant utilisable depuis ${(_c = this.kernel) === null || _c === void 0 ? void 0 : _c.languageName()}`);
                resolve();
            };
            reader.onerror = reject;
        });
    }
    /**
     * Load auxiliary files submited via URL (aux= parameter) (async).
     */
    async loadURLAux() {
        if (this.kernelSafe == null)
            return;
        await this._loadFromURL('aux', this.kernelSafe.putFile.bind(this.kernelSafe));
    }
    /**
     * Load modules submited via URL (module= parameter) (async).
     */
    async loadURLModules() {
        if (this.kernelSafe == null)
            return;
        await this._loadFromURL('module', this.kernelSafe.putModule.bind(this.kernelSafe));
    }
    /**
     * Opening file: If it has ext as extension, loading it in
     * the editor or put on (emulated) local filesystem
     * (user is asked to), otherwise, loading it in the local
     * filesystem.
     */
    _openFile(callbacks) {
        return new Promise((resolve, reject) => {
            let input = document.createElement('input');
            input.type = 'file';
            input.style.display = "none";
            input.onchange = async () => {
                if (input.files == null)
                    return;
                for (let file of input.files) {
                    const ext = file.name.split('.').pop();
                    const callback = callbacks[ext !== null && ext !== void 0 ? ext : ""];
                    if (callback != null) {
                        await callback(file);
                    }
                    else {
                        await this.putFSRessource(file);
                    }
                }
                resolve();
            };
            input.onerror = reject;
            document.body.appendChild(input);
            input.click();
            document.body.removeChild(input);
        });
    }
    /**
     * Open an URL in a new tab or download a file.
     */
    static openURL(url, download) {
        let anchor = document.createElement("a");
        if (download != null)
            anchor.download = download;
        anchor.href = url;
        anchor.target = "_blank";
        anchor.style.display = "none"; // just to be safe!
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
    }
    /**
     * Share content via URL.
     */
    async _share(url, message) {
        this.confirm("Partager ce notebook", message, "Copier dans le presse-papier", () => GUIBase.copyToClipboard(url), "Tester le lien", () => GUIBase.openURL(url));
    }
    /**
     * Binding to kernel's XHR.
     */
    static async xhr(params) {
        return await KernelBase.xhr(params);
    }
    /**
     * Compress a string to another string (URL safe).
     */
    async deflate(content) {
        // dynamic import for webpack
        const pako = await import("pako");
        const { Base64 } = await import("js-base64");
        return Base64.fromUint8Array(pako.deflate(content), true);
    }
    /**
     * Reverse version of deflate.
     */
    async inflate(content) {
        // dynamic import for webpack
        const pako = await import("pako");
        const { Base64 } = await import("js-base64");
        return pako.inflate(Base64.toUint8Array(content), { to: 'string' });
    }
}
/**
 * Copy a text to clipboard.
 */
GUIBase.copyToClipboard = function (text) {
    if (navigator.clipboard != null)
        navigator.clipboard.writeText(text);
    else {
        // using the deprecated API document.execCommand('copy')
        let textArea = document.createElement("textarea");
        // Precautions from https://stackoverflow.com/questions/400212/how-do-i-copy-to-the-clipboard-in-javascript
        // Place in top-left corner of screen regardless of scroll position.
        textArea.style.position = 'fixed';
        textArea.style.top = "0px";
        textArea.style.left = "0px";
        // Ensure it has a small width and height. Setting to 1px / 1em
        // doesn't work as this gives a negative w/h on some browsers.
        textArea.style.width = '2em';
        textArea.style.height = '2em';
        // We don't need padding, reducing the size if it does flash render.
        textArea.style.padding = "0px";
        // Clean up any borders.
        textArea.style.border = 'none';
        textArea.style.outline = 'none';
        textArea.style.boxShadow = 'none';
        // Avoid flash of white box if rendered for any reason.
        textArea.style.background = 'transparent';
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
            let successful = document.execCommand('copy');
            let msg = successful ? 'successful' : 'unsuccessful';
            console.log('Copying text command was ' + msg);
        }
        catch (err) {
            console.log('Oops, unable to copy');
        }
        document.body.removeChild(textArea);
    }
};
